import { useEffect, useState } from 'react';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import {
  InfoCard,
  Link,
  Progress,
  StatusError,
  StatusOK,
  StatusWarning,
} from '@backstage/core-components';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';

// Shape returned by the backend's /api/openbao/.../metadata
// (packages/backend/src/plugins/openbao).
export type OpenbaoPathRow = {
  environment: string;
  kind: 'config' | 'secret';
  mount: string;
  path: string;
  uiUrl?: string;
  found: boolean;
  error?: string;
  currentVersion?: number;
  updatedTime?: string;
  versionCount?: number;
  currentDeleted?: boolean;
};

export type OpenbaoMetadata = {
  basePath: string;
  environments: string[];
  paths: OpenbaoPathRow[];
};

// Status cell for one path. Values are never shown: to see or change them,
// the link opens the OpenBao UI, where you sign in with your own role.
export const rowStatus = (row: OpenbaoPathRow) => {
  if (row.error) return { level: 'error' as const, text: row.error };
  if (!row.found) return { level: 'warning' as const, text: 'Not in OpenBao' };
  if (row.currentDeleted) {
    return { level: 'error' as const, text: `v${row.currentVersion} deleted` };
  }
  return {
    level: 'ok' as const,
    text: `v${row.currentVersion} of ${row.versionCount}`,
  };
};

const Status = ({ row }: { row: OpenbaoPathRow }) => {
  const { level, text } = rowStatus(row);
  if (level === 'ok') return <StatusOK>{text}</StatusOK>;
  if (level === 'warning') return <StatusWarning>{text}</StatusWarning>;
  return <StatusError>{text}</StatusError>;
};

// OpenBao card on the Overview tab of services: per environment, the
// service's config and secret paths, their current version and last update.
// Hidden when OpenBao is not configured in Backstage.
export const OpenbaoCard = () => {
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [data, setData] = useState<OpenbaoMetadata>();
  const [failed, setFailed] = useState(false);

  const entityPath = `entities/${encodeURIComponent(
    entity.metadata.namespace ?? 'default',
  )}/${encodeURIComponent(entity.kind)}/${encodeURIComponent(
    entity.metadata.name,
  )}`;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('openbao');
        const res = await fetchApi.fetch(`${baseUrl}/${entityPath}/metadata`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = (await res.json()) as OpenbaoMetadata;
        if (!cancelled) setData(body);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [discoveryApi, fetchApi, entityPath]);

  if (failed || (data && data.environments.length === 0)) {
    return null;
  }

  return (
    <InfoCard
      title="OpenBao"
      subheader={
        data
          ? `config/${data.basePath}/config · secret/${data.basePath}/secret`
          : undefined
      }
    >
      {!data ? (
        <Progress />
      ) : (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Environment</TableCell>
                <TableCell>Path</TableCell>
                <TableCell>Version</TableCell>
                <TableCell>Last updated</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {data.paths.map(row => (
                <TableRow key={`${row.environment}-${row.kind}`}>
                  <TableCell>{row.environment.toUpperCase()}</TableCell>
                  <TableCell>{row.kind}</TableCell>
                  <TableCell>
                    <Status row={row} />
                  </TableCell>
                  <TableCell>
                    {row.updatedTime
                      ? new Date(row.updatedTime).toLocaleString()
                      : '–'}
                  </TableCell>
                  <TableCell>
                    {row.found && row.uiUrl && (
                      <Link to={row.uiUrl}>Open in OpenBao</Link>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography variant="caption" color="textSecondary">
            Values are not shown here. Open in OpenBao and sign in with your own
            role to view or change them; changes reach the pods after a Sync.
          </Typography>
        </>
      )}
    </InfoCard>
  );
};
